import { useRef, useState } from "react";
import clsx from "clsx";
import { ArrowRight, ChevronDown, Download, FileSpreadsheet, RotateCcw, Upload } from "lucide-react";

import { API_APPRAISAL_V2 } from "../../../../../config/Endpoints.js";
import type { EmployeePersonalDetail } from "../../../../schemas/employee-profile.schema.ts";
import { postFormDataMultipart } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { useEmployeePhotoSource } from "../../employee-view-directory/hooks/use-employee-photo-source.ts";
import { Card } from "../../../shared/card/card.tsx";
import { Avatar, Button, DatePicker, DatePickerFieldTrigger, Modal, Select } from "../../../ui/index.ts";
import type { SelectOption } from "../../../ui/select/select.tsx";
import { LETTER_TYPE_OPTIONS } from "../letter-type-options.ts";
import type { EmployeeAppraisalValues } from "../types.ts";
import styles from "../employee-appraisal.module.css";

const NOTICE_PERIOD_OPTIONS = [30,45, 60, 90].map((days) => ({ value: `${days} Days`, label: `${days} days` }));

type Props = {
  values: EmployeeAppraisalValues;
  employeeOptions: SelectOption[];
  employeesLoading: boolean;
  selectedEmployeeName: string;
  personalDetail: EmployeePersonalDetail | null;
  profileLoading: boolean;
  designationOptions: SelectOption[];
  designationsLoading: boolean;
  onChange: <K extends keyof EmployeeAppraisalValues>(key: K, value: EmployeeAppraisalValues[K]) => void;
  onLetterIssueDateChange: (date: Date | null) => void;
  onReset: () => void;
  onContinue: () => void;
};

type BulkUploadError = { rowNumber?: number; email?: string; error?: string };
type BulkUploadResult = {
  totalRows?: number;
  successCount?: number;
  failedCount?: number;
  errors?: BulkUploadError[];
};

function normalized(value?: string): string {
  return value?.trim().toLocaleLowerCase() ?? "";
}

export function BasicDetailsStep({ values, employeeOptions, employeesLoading, selectedEmployeeName, personalDetail, profileLoading, designationOptions, designationsLoading, onChange, onLetterIssueDateChange, onReset, onContinue }: Props) {
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [bulkUploadStatus, setBulkUploadStatus] = useState<"DRAFT" | "PENDING_APPROVAL" | null>(null);
  const [bulkUploadMenuOpen, setBulkUploadMenuOpen] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState<BulkUploadResult | null>(null);
  const bulkUploadInputRef = useRef<HTMLInputElement>(null);
  const selectedLetter = LETTER_TYPE_OPTIONS.find((option) => option.value === values.letterType) ?? LETTER_TYPE_OPTIONS[0];
  const employeePhoto = useEmployeePhotoSource(personalDetail?.photoPath);
  const designationDisabled = !values.employeeId || profileLoading || !personalDetail?.departmentId || designationsLoading;

  const showDesignationDisabledReason = () => {
    if (!values.employeeId) showError("Select an employee before choosing a designation.", { id: "appraisal-designation-employee-required" });
    else if (profileLoading) showError("Employee details are still loading. Please wait.", { id: "appraisal-designation-profile-loading" });
    else if (!personalDetail?.departmentId) showError("The selected employee does not have a department configured.", { id: "appraisal-designation-department-required" });
    else if (designationsLoading) showError("Designations are still loading. Please wait.", { id: "appraisal-designations-loading" });
  };

  const changeLetterType = (letterType: EmployeeAppraisalValues["letterType"]) => {
    if (letterType === values.letterType) return;
    onChange("letterType", letterType);
    onChange("letterIssueDate", null);
    onChange("effectiveDate", null);
    onChange("noticePeriod", "90 Days");
    onChange("designationId", "");
    onChange("designation", "");
  };

  const closeBulkUpload = () => {
    if (bulkUploadStatus) return;
    setBulkUploadOpen(false);
    setBulkUploadFile(null);
    setBulkUploadMenuOpen(false);
    setBulkUploadResult(null);
    if (bulkUploadInputRef.current) bulkUploadInputRef.current.value = "";
  };

  const downloadBulkUploadFormat = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL ?? ""}/assets/Appraisal_Bulk_Format.xlsx`;
    link.download = "Appraisal_Bulk_Format.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const uploadBulkAppraisals = async (status: "DRAFT" | "PENDING_APPROVAL") => {
    if (!bulkUploadFile) {
      showError("Select the completed bulk-upload Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", bulkUploadFile);
    setBulkUploadMenuOpen(false);
    setBulkUploadStatus(status);
    try {
      const { data } = await postFormDataMultipart<{ message?: string; data?: BulkUploadResult }>(
        `${API_APPRAISAL_V2}/bulk-upload?status=${status}`,
        accessToken(),
        formData,
      );
      const result = data.data;
      const failedCount = result?.failedCount ?? result?.errors?.length ?? 0;
      setBulkUploadStatus(null);
      if (failedCount > 0) {
        setBulkUploadResult(result ?? null);
        showError(`${failedCount} appraisal row${failedCount === 1 ? "" : "s"} could not be uploaded. Review the errors in the modal.`);
      } else {
        showSuccess(data.message ?? (status === "DRAFT"
          ? "Appraisals uploaded as drafts successfully."
          : "Appraisals uploaded and submitted for approval successfully."));
        closeBulkUpload();
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to upload appraisal data.");
      setBulkUploadStatus(null);
    }
  };

  return <>
   <Card padding="5xl" className={styles.section}>
      <div className={styles.employeeGrid}>
        <div className={styles.employeeControlRow}>
          <div className={styles.employeeSelectControl}>
            <Select id="appraisal-employee" label="Employee Name" required searchable isClearable value={values.employeeId} options={employeeOptions} placeholder={employeesLoading ? "Loading Employees..." : "Search by Employee Name"} disabled={employeesLoading} onChange={(value) => {
              onChange("employeeId", value);
              onChange("designationId", "");
              onChange("designation", "");
            }} />
          </div>
          {values.employeeId ? <div className={styles.employeeCard}>
            <Avatar name={selectedEmployeeName} src={employeePhoto} size="lg" />
            <div><strong>{profileLoading ? "Loading employee details..." : selectedEmployeeName}</strong><div className={styles.employeeMeta}><span>{personalDetail?.designation ?? "-"}</span><span>{personalDetail?.ccEmail ?? "-"}</span></div></div>
          </div> : null}
          <Button variant="muted-outline" startIcon={<Upload size={16} />} onClick={() => setBulkUploadOpen(true)}>Bulk Upload</Button>
        </div>
      </div>
    </Card>

    <div className={styles.letterGrid}>
      {LETTER_TYPE_OPTIONS.map(({ value, title, description, icon: Icon, tone }) => <button key={value} type="button" className={clsx(styles.letterCard, values.letterType === value && styles.letterCardSelected)} onClick={() => changeLetterType(value)} aria-pressed={values.letterType === value}>
        <span className={clsx(styles.letterIcon, styles[`tone${tone[0].toUpperCase()}${tone.slice(1)}`])}><Icon size={22} /></span>
        <span className={styles.letterCopy}><strong>{title}</strong><span>{description}</span></span>
      </button>)}
    </div>

    <Modal
      open={bulkUploadOpen}
      onClose={closeBulkUpload}
      title="Bulk Upload Appraisals"
      size="md"
      closeOnEscape
      footer={<div className={styles.bulkUploadFooter}>
        <Button variant="light-grey" disabled={bulkUploadStatus != null} onClick={closeBulkUpload}>Cancel</Button>
        <div className={styles.bulkUploadActionMenu}>
          <Button startIcon={<Upload size={16} />} disabled={!bulkUploadFile || bulkUploadStatus != null} onClick={() => setBulkUploadMenuOpen((open) => !open)}>
            {bulkUploadStatus === "DRAFT" ? "Saving..." : bulkUploadStatus === "PENDING_APPROVAL" ? "Submitting..." : "Upload"}
            {bulkUploadStatus == null ? <ChevronDown size={15} aria-hidden /> : null}
          </Button>
          {bulkUploadMenuOpen ? <div className={styles.bulkUploadMenu} role="menu">
            <button type="button" role="menuitem" onClick={() => void uploadBulkAppraisals("DRAFT")}>Save as Draft</button>
            <button type="button" role="menuitem" onClick={() => void uploadBulkAppraisals("PENDING_APPROVAL")}>Submit for Approval</button>
          </div> : null}
        </div>
      </div>}
    >
      <div className={styles.bulkUploadModal}>
        <div className={styles.bulkUploadIntro}>
          <div><h3>Upload completed appraisal data</h3><p>Download the Excel format, complete the required fields, and select the completed file below.</p></div>
          <Button size="sm" variant="muted-outline" startIcon={<Download size={15} />} onClick={downloadBulkUploadFormat}>Download Format</Button>
        </div>
        <input
          ref={bulkUploadInputRef}
          className={styles.bulkUploadInput}
          id="appraisal-bulk-upload-file"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={bulkUploadStatus != null}
          onChange={(event) => { setBulkUploadFile(event.target.files?.[0] ?? null); setBulkUploadResult(null); }}
        />
        <button type="button" className={styles.bulkUploadDropzone} onClick={() => bulkUploadInputRef.current?.click()}>
          <FileSpreadsheet size={30} aria-hidden />
          <strong>{bulkUploadFile?.name ?? "Choose Excel file"}</strong>
          <span>{bulkUploadFile ? "Click to replace the selected file" : "Only .xlsx files are supported"}</span>
        </button>
        {bulkUploadResult?.errors?.length ? <section className={styles.bulkUploadErrors} aria-live="polite">
          <div className={styles.bulkUploadErrorHeader}>
            <div><strong>Upload completed with errors</strong><span>{bulkUploadResult.successCount ?? 0} successful · {bulkUploadResult.failedCount ?? bulkUploadResult.errors.length} failed</span></div>
            <span>{bulkUploadResult.totalRows ?? bulkUploadResult.errors.length} rows</span>
          </div>
          <ul>{bulkUploadResult.errors.map((item, index) => <li key={`${item.rowNumber ?? index}-${item.email ?? "row"}`}>
            <div><strong>Row {item.rowNumber ?? index + 1}</strong>{item.email ? <span>{item.email}</span> : null}</div>
            <p>{item.error || "This row could not be uploaded."}</p>
          </li>)}</ul>
        </section> : null}
      </div>
    </Modal>

   
    <Card padding="5xl" className={styles.section}>
      <div className={styles.fieldsGrid}>
        <DatePicker id="appraisal-letter-issue-date" label="Letter Issue Date" required selected={values.letterIssueDate} onChange={onLetterIssueDateChange} customInput={<DatePickerFieldTrigger placeholderText="Select date" />} />
        <DatePicker id="appraisal-effective-date" label="Effective Date" required selected={values.effectiveDate} onChange={(date) => onChange("effectiveDate", date)} customInput={<DatePickerFieldTrigger placeholderText="Select Date" />} />
        {selectedLetter.fields.compensation ? <Select id="appraisal-notice-period" label="Notice Period" value={values.noticePeriod} options={NOTICE_PERIOD_OPTIONS} placeholder="Select Notice Period" onChange={(value) => onChange("noticePeriod", value)} /> : null}
        {selectedLetter.fields.designation ? <div onPointerDownCapture={(event) => { if (designationDisabled) { event.preventDefault(); showDesignationDisabledReason(); } }}><Select id="appraisal-designation" label="Designation" required searchable value={values.designationId} options={designationOptions} placeholder={designationsLoading ? "Loading designations..." : "Select Designation"} disabled={designationDisabled} onChange={(value) => {
          const designation = designationOptions.find((option) => option.value === value)?.label ?? "";
          if (designation && normalized(designation) === normalized(personalDetail?.designation)) {
            showError("The employee's current designation cannot be selected. Choose a different designation.", { id: "appraisal-current-designation-not-allowed" });
            onChange("designationId", "");
            onChange("designation", "");
            return;
          }
          onChange("designationId", value);
          onChange("designation", designation);
        }} /></div> : null}
      
      </div>
    </Card>
    <div className={styles.actions}><Button variant="muted-outline" startIcon={<RotateCcw size={16} />} onClick={onReset}>Reset</Button><Button onClick={onContinue}>Continue <ArrowRight size={16} /></Button></div>
  </>;
}
